// Part One — every activity as a light, data-driven placeholder.
// Sequence, naming and format are deliberately easy to change here.
// mode: "reflect" (you write, Spark listens) · "notice" (Spark surfaces a pattern, you check it) · "act" (you choose a small step)
export const STAGES = [
  { key: "start-here", label: "Start Here", question: "Why am I here?" },
  { key: "understand-me", label: "Understand Me", question: "Where am I now?" },
  { key: "what-i-bring", label: "What I Bring", question: "What do I bring?" },
  { key: "unblock-thinking", label: "Unblock Thinking", question: "What's keeping me stuck?" },
  { key: "work-out-what-i-want", label: "Work Out What I Want", question: "What do I want next?" },
  { key: "explore-options", label: "Explore Options", question: "What could I do?" },
  { key: "choose-direction", label: "Choose Direction", question: "Where am I heading?" },
];

export const MODES = {
  reflect: {
    label: "Reflect",
    tagline: "You write. Spark listens, then offers one quiet observation.",
    rhythm: [
      { who: "you", text: "You write — rough is fine" },
      { who: "spark", text: "Spark reads between the lines" },
      { who: "spark", text: "Offers what it's noticing, with the evidence" },
      { who: "you", text: "You say Yes, Partly or No" },
    ],
    inputLabel: "Start here, if you like",
    placeholder: "Write what comes to mind. Spark will read it and check its understanding with you.",
  },
  notice: {
    label: "Notice",
    tagline: "Spark brings together what you've already said and shows you the pattern.",
    rhythm: [
      { who: "spark", text: "Spark gathers what you've confirmed so far" },
      { who: "spark", text: "Names a possible pattern — tentatively" },
      { who: "you", text: "You check it against your own experience" },
      { who: "you", text: "Yes, Partly or No — and it's held or set aside" },
    ],
    inputLabel: "Anything to add before Spark looks?",
    placeholder: "Optional. A detail, a correction, something that's changed.",
  },
  act: {
    label: "Act",
    tagline: "A small, real step — chosen by you, sized to fit the next two weeks.",
    rhythm: [
      { who: "spark", text: "Spark suggests where a small step could sit" },
      { who: "you", text: "You choose one — or write your own" },
      { who: "you", text: "You name when you'll check back" },
      { who: "spark", text: "Spark holds it and asks how it went" },
    ],
    inputLabel: "One small step",
    placeholder: "Something you could actually do in the next two weeks.",
  },
};

export const ACTIVITIES = [
  {
    key: "my-spark-starting-point", stage: "Start Here", mode: "reflect", title: "My Spark Starting Point", minutes: 5,
    focus: "why you came to Spark",
    blurb: "Why am I here and what do I want from this?",
    opening: "Before we look at anything else, let's be honest about why you're here.",
    why: "Everything Spark notices later is read against this. It's the difference between advice and coaching.",
    questions: [],
    feeds: ["starting_point"],
  },
  {
    key: "career-pulse", stage: "Understand Me", mode: "reflect", title: "Career Pulse", minutes: 8,
    focus: "how work is sitting with you right now",
    blurb: "A quiet check across meaning, energy, growth, autonomy and eight more.",
    opening: "A number on its own doesn't say much. What sits behind it usually does.",
    why: "Your first read on where work is working and where it isn't — Spark uses it to notice what's loudest.",
    questions: [],
    feeds: ["energy_sources", "energy_drains", "values"],
  },
  {
    key: "career-timeline", stage: "Understand Me", mode: "reflect", title: "Career Timeline", minutes: 12,
    focus: "the turning points in your career",
    blurb: "Three to five significant career moments — what shifted and why.",
    opening: "Careers rarely move in straight lines. I'm curious about the turns.",
    why: "Turning points show what you move towards and what you move away from — often more honestly than a job description.",
    questions: [
      "Pick one moment when work felt like it was going somewhere. What were you doing?",
      "A decision that changed your direction — what did it cost, or give you?",
      "A harder stretch. Looking back, what was missing, or too much?",
    ],
    feeds: ["career_highs", "career_lows", "values"],
  },
  {
    key: "energy-map", stage: "What I Bring", mode: "notice", title: "Energy Map", minutes: 10,
    focus: "what gives you energy at work",
    blurb: "What lights you up, what drains you, and where you'd like more or less.",
    opening: "Energy tends to tell the truth before we're ready to say it.",
    why: "Where your energy goes — and where it leaks — is one of the clearest signals of fit.",
    questions: [
      "Something at work that consistently gives you energy, even when it's hard. What is it about it, really?",
      "Something that leaves you flat, however small. What's the pattern underneath?",
    ],
    feeds: ["energy_sources", "energy_drains"],
  },
  {
    key: "strengths-evidence-map", stage: "What I Bring", mode: "notice", title: "Strengths Evidence Map", minutes: 12,
    focus: "the strengths people quietly rely on you for",
    blurb: "Me at my best — with evidence, not labels.",
    opening: "Not the strengths on your CV. The ones people quietly rely on you for.",
    why: "Strengths grounded in evidence hold up when confidence wobbles — and they're what your Value Proposition is built from later.",
    questions: [
      "Spark will gather what you've said in your Timeline and Energy Map and look for what keeps showing up.",
      "You'll see each possible strength with the evidence beside it — and decide whether it's really yours.",
    ],
    feeds: ["strengths"],
  },
  {
    key: "achievement-mining", stage: "What I Bring", mode: "reflect", title: "Achievement Mining", minutes: 10,
    focus: "what you've done that was harder than it looked",
    blurb: "The things you made better, harder than they looked.",
    opening: "Most people undersell what they've done because they remember how it felt, not what it took.",
    why: "Achievements with the difficulty left in become your Confidence Bank and your career story.",
    questions: [
      "Something difficult you handled. What was actually hard about it — the part nobody else saw?",
      "Something that was better because you were there. How do you know?",
    ],
    feeds: ["confidence_evidence", "strengths"],
  },
  {
    key: "values-in-action", stage: "Unblock Thinking", mode: "notice", title: "Values in Action", minutes: 10,
    focus: "when work has felt right, and when it hasn't",
    blurb: "The times work felt right — and the times it didn't.",
    opening: "Values show up most clearly when something is missing.",
    why: "Naming the values that were present, or absent, gives you language for what 'right' means for you.",
    questions: [
      "A time work felt particularly good. What was present that mattered?",
      "A time work felt wrong, even if it looked fine from outside. What was being compromised?",
    ],
    feeds: ["values"],
  },
  {
    key: "confidence-inner-narrative", stage: "Unblock Thinking", mode: "reflect", title: "Confidence & Inner Narrative", minutes: 8,
    focus: "the sentences you tell yourself",
    blurb: "The sentences you keep telling yourself.",
    opening: "We all carry sentences about ourselves that were written a long time ago.",
    why: "Surfacing the narrative lets you examine it — instead of quietly planning around it.",
    questions: [
      "Finish the sentence: I'd be more confident about my career if…",
      "A belief about yourself at work you suspect isn't fully true anymore. Where did it come from?",
    ],
    feeds: ["limiting_narratives"],
  },
  {
    key: "fact-story-fear", stage: "Unblock Thinking", mode: "notice", title: "Fact, Story or Fear?", minutes: 8,
    focus: "what's true and what's assumed",
    blurb: "Separating what's true from what's assumed.",
    opening: "Some of what's holding you in place is real. Some of it is a story. Let's tell them apart.",
    why: "A fear you've named is easier to act around than a fear you're steering by.",
    questions: [
      "Spark takes the sentences from your Inner Narrative and sorts each one — fact, story or fear — with its reasoning shown.",
      "You move anything it's got wrong. What's left as 'fact' becomes something you can actually test.",
    ],
    feeds: ["limiting_narratives"],
  },
  {
    key: "my-confidence-bank", stage: "Unblock Thinking", mode: "notice", title: "My Confidence Bank", minutes: 6,
    focus: "the evidence of what you've actually done",
    blurb: "A growing record of what you've actually done.",
    opening: "Confidence built on evidence is quieter than bravado — and it lasts.",
    why: "This is where the achievements, the strengths and the hard things go so they're there when you need them.",
    questions: [
      "Spark collects the evidence you've already given it — achievements, strengths, hard things handled — and shows it in one place.",
      "You add what's missing, and remove anything that doesn't feel earned.",
    ],
    feeds: ["confidence_evidence"],
  },
  {
    key: "conditions-for-thriving", stage: "Work Out What I Want", mode: "reflect", title: "Conditions for Thriving", minutes: 10,
    focus: "the conditions that let you do your best work",
    blurb: "The environments that let you do your best work.",
    opening: "You don't need to be a different person. You may need different conditions.",
    why: "Knowing your conditions turns 'I want something better' into something you can actually look for.",
    questions: [
      "The environment where you've done your best work — pace, people, purpose, permission.",
      "An environment where you shrank. What, specifically, made it hard to be good there?",
    ],
    feeds: ["conditions_for_thriving"],
  },
  {
    key: "more-less-keep", stage: "Work Out What I Want", mode: "act", title: "More / Less / Keep", minutes: 6,
    focus: "what to change and what to keep",
    blurb: "The three changes that would make the biggest difference.",
    opening: "Not everything needs to change. Let's find out what does.",
    why: "It's easy to assume a big move is needed. Sometimes three small shifts get you most of the way.",
    questions: [
      "One thing you'd want more of. One less of. One to keep, exactly as it is.",
      "Then: which of the three could you start on this week?",
    ],
    feeds: ["career_criteria"],
  },
  {
    key: "future-without-job-titles", stage: "Work Out What I Want", mode: "reflect", title: "Future Without Job Titles", minutes: 10,
    focus: "a future without job titles",
    blurb: "Three years from now, without naming a role.",
    opening: "Job titles narrow the imagination. Let's leave them out for a moment.",
    why: "Describing the texture of a working life — not its label — reveals criteria you didn't know you had.",
    questions: [
      "Three years from now — what are you doing on a Tuesday afternoon? Who's around? What's the pace?",
      "Looking back from there, what are you quietly proud of having built or changed?",
    ],
    feeds: ["career_criteria", "possibilities"],
  },
  {
    key: "career-non-negotiables", stage: "Work Out What I Want", mode: "notice", title: "Career Non-Negotiables", minutes: 8,
    focus: "what you won't trade",
    blurb: "Must have, strong preference, nice to have, deal-breaker.",
    opening: "Clarity about what you won't trade makes every other decision easier.",
    why: "These become the filter for every option you weigh in Part Two.",
    questions: [
      "Spark drafts your criteria from your Values, Conditions and More / Less / Keep — sorted into must have, strong preference, nice to have, deal-breaker.",
      "You move them. The list is only finished when it's yours.",
    ],
    feeds: ["career_criteria"],
  },
  {
    key: "the-career-crossroads", stage: "Explore Options", mode: "reflect", title: "The Career Crossroads", minutes: 8,
    focus: "the six kinds of move open to you",
    blurb: "Stay · Reshape · Step Up · Move · Pivot · Reinvent.",
    opening: "There are more than two options. There are usually six.",
    why: "Seeing the full set of moves stops the conversation collapsing into 'stay or leave'.",
    questions: [
      "Of Stay, Reshape, Step Up, Move, Pivot and Reinvent — which are you drawn to, and which do you avoid?",
      "What would have to be true for the one you're avoiding to be worth considering?",
    ],
    feeds: ["possibilities"],
  },
  {
    key: "possibility-map", stage: "Explore Options", mode: "reflect", title: "Possibility Map", minutes: 10,
    focus: "the possibilities, obvious and secret",
    blurb: "The obvious, the adjacent, the ambitious, the secret.",
    opening: "The secret option is usually the interesting one.",
    why: "Widening the field before narrowing it is how good decisions get made.",
    questions: [
      "The next step everyone would expect you to take.",
      "Something one step sideways. Something ambitious. Something you've never said out loud.",
    ],
    feeds: ["possibilities"],
  },
  {
    key: "transferable-capability-map", stage: "Explore Options", mode: "notice", title: "Transferable Capability Map", minutes: 8,
    focus: "what travels with you",
    blurb: "What you carry with you, wherever you go next.",
    opening: "Your capabilities aren't owned by your job title. They travel.",
    why: "Seeing what transfers makes options that felt out of reach look closer than they did.",
    questions: [
      "Spark lays your confirmed strengths against the possibilities you've mapped and shows where they already carry.",
      "You'll see the gaps too — honestly sized, not inflated.",
    ],
    feeds: ["strengths", "possibilities"],
  },
  {
    key: "test-dont-decide", stage: "Explore Options", mode: "act", title: "Test, Don't Decide", minutes: 6,
    focus: "a small experiment",
    blurb: "Small experiments before big commitments.",
    opening: "You don't have to decide. You could find out.",
    why: "A two-week experiment tells you more than two months of thinking.",
    questions: [
      "One small experiment — a conversation, a shadow day, a side project — that would teach you something about an option.",
      "What would you know afterwards that you don't know now?",
    ],
    feeds: ["action_plan"],
  },
  {
    key: "then-vs-now", stage: "Choose Direction", mode: "notice", title: "Then vs Now", minutes: 8,
    focus: "how your thinking has shifted",
    blurb: "What has changed in your thinking since you started?",
    opening: "You're not the same person who wrote your Starting Point. Let's see how.",
    why: "Naming the shift is how the learning sticks — and how a direction becomes yours rather than Spark's.",
    questions: [
      "Spark sets your Starting Point beside everything you've confirmed since, and offers a sentence about your direction.",
      "You rewrite it until it sounds like you.",
    ],
    feeds: ["direction"],
  },
];

export const FEED_LABELS = {
  starting_point: "Why I came to Spark",
  strengths: "My Strengths",
  values: "My Values",
  energy_sources: "What gives me energy",
  energy_drains: "What drains me",
  conditions_for_thriving: "Conditions for Thriving",
  limiting_narratives: "What I'm working through",
  confidence_evidence: "My Confidence Bank",
  career_highs: "Career highs",
  career_lows: "Career lows",
  career_criteria: "My criteria",
  possibilities: "My possibilities",
  action_plan: "My next moves",
  direction: "My Direction",
};

export const activityPath = (a) => a.key === "my-spark-starting-point" ? "/starting-point" : `/part-one/${a.key}`;
